import {
  commentFooterClass,
  commentTranslateButtonClass,
} from "@repo/fika-blog/src/comments.css"
import type { JSONContent } from "@repo/fika-blog/src/tiptap/core"
import {
  getLanguageLabel,
  getLocaleDisplayName,
} from "@repo/fika-shared/src/locale.js"
import { createPolled } from "@solid-primitives/timer"
import { useParams } from "@solidjs/router"
import { createMutation, createQuery } from "@urql/solid"
import { createEffect, createMemo, createSignal, For, on, Show } from "solid-js"
import { createSession } from "@/context/session-provider"
import { Button } from "@/designSystem/components/button"
import { Icon } from "@/designSystem/components/icon"
import { MainLayout } from "@/designSystem/layouts/main-layout"
import { Navigation } from "@/designSystem/patterns/navigation"
import { RichContent } from "@/designSystem/patterns/rich-content"
import { createFragment, type FragmentOf, graphql } from "@/graphql"
import { useI18nContext } from "@/i18n/i18n-solid"
import {
  createMutationWithErrorHandling,
  ErrorFragment,
} from "@/lib/graphql-mutation-helpers"
import { useRelativeTime } from "@/lib/use-relative-time"
import { SimpleWysiwygEditor } from "@/pages/posts/editor/simple-wysiwyg"
import { ThreadParticipants } from "./_thread-participants"
import {
  containerClass,
  messageCardClass,
  messageContentClass,
  messageDateClass,
  messageHeaderClass,
  messagesListClass,
  postPublicationNameClass,
  postReferenceInlineClass,
  postReferenceLabelClass,
  replyActionsClass,
  replyEditorWrapperClass,
  replyErrorClass,
  replyFormClass,
  threadInfoClass,
  threadTypeBannerClass,
} from "./thread-view.css"

const MessageCardFragment = graphql(`
  fragment MessageCardFragment on Message {
    id
    content
    locale
    createdAt
    author {
      id
      isMe
      authorName
      title
      username
      email
      profileImage
      publicationUrl
    }
  }
`)

const TranslateMessageMutation = graphql(
  `mutation TranslateInboxMessage($messageId: String!, $toLocale: Locale!) {
    translateMessage(messageId: $messageId, toLocale: $toLocale) {
      ... on MutationTranslateMessageSuccess {
        data {
          translatedContent
        }
      }
      ... on BaseError {
        ...ErrorFragment
      }
    }
  }`,
  [ErrorFragment],
)

const ThreadDetailFragment = graphql(
  `fragment ThreadDetailFragment on Thread {
    id
    postId
    subject
    private
    createdAt
    post {
      id
      title
      description
      imageUrl
      url
      publication {
        id
        name
      }
    }
    messages {
      id
      read
      ...MessageCardFragment
    }
  }`,
  [MessageCardFragment],
)

const SendMessageMutation = graphql(
  `mutation SendMessage($threadId: String!, $content: String!) {
    sendMessage(threadId: $threadId, content: $content) {
      ... on MutationSendMessageSuccess {
        data {
          ...ThreadDetailFragment
        }
      }
      ... on FlaggedMessageError {
        ...ErrorFragment
      }
    }
  }`,
  [ThreadDetailFragment, ErrorFragment],
)

export function ThreadView() {
  const params = useParams<{ threadId: string }>()
  const { LL } = useI18nContext()
  const { session } = createSession()
  const [replyContent, setReplyContent] = createSignal<JSONContent | null>(null)
  const [replyEditorKey, setReplyEditorKey] = createSignal(0)
  const [sendErrorMessage, setSendErrorMessage] = createSignal<string | null>(
    null,
  )

  const [result, reexecute] = createQuery({
    query: graphql(
      `query Thread($id: String!) {
        thread(id: $id) {
          ...ThreadDetailFragment
        }
      }`,
      [ThreadDetailFragment],
    ),
    variables: () => ({ id: params.threadId }),
  })

  createPolled(() => reexecute(), 15_000)

  const [, markRead] = createMutation(
    graphql(`
      mutation MarkThreadRead($threadId: String!) {
        markThreadRead(threadId: $threadId)
      }
    `),
  )
  const [sendResult, sendMessage] = createMutationWithErrorHandling(
    SendMessageMutation,
    "sendMessage",
  )

  const thread = createFragment(ThreadDetailFragment, () => result.data?.thread)

  const threadHue = createMemo(() =>
    thread()?.private === false ? "sand" : "rose",
  )

  let firstUnreadEl: HTMLDivElement | undefined

  const firstUnreadIndex = createMemo(() => {
    const t = thread()
    if (!t) return -1
    return t.messages.findIndex(m => !m.read)
  })

  createEffect(() => {
    if (firstUnreadIndex() < 0) return
    queueMicrotask(() => {
      firstUnreadEl?.scrollIntoView({ behavior: "smooth", block: "start" })
    })
  })

  createEffect(
    on(
      () => thread()?.messages.at(-1)?.id,
      () => markRead({ threadId: params.threadId }),
    ),
  )

  createEffect(
    on(
      () => params.threadId,
      () => {
        setReplyContent(null)
        setReplyEditorKey(0)
      },
    ),
  )

  const canSubmitReply = createMemo(() => {
    const content = replyContent()
    if (!content || sendResult.fetching) return false
    const text = (content.content ?? [])
      .flatMap(node => (node.content ?? []).map(n => n.text ?? ""))
      .join("")
      .trim()
    return text !== ""
  })

  async function handleSubmit() {
    setSendErrorMessage(null)
    const content = replyContent()
    if (!content || !canSubmitReply()) return

    const out = await sendMessage(
      {
        threadId: params.threadId,
        content: JSON.stringify(content),
      },
      { muteGraphQlUnionToast: true },
    )

    if (out.success) {
      setReplyContent(null)
      setReplyEditorKey(k => k + 1)
    } else if (out.error.message === "FLAGGED_MESSAGE") {
      setSendErrorMessage(LL().comments.flaggedMessage())
    }
  }

  return (
    <MainLayout
      hue={threadHue()}
      header={
        <Navigation
          title={LL().inbox.message()}
          hue={threadHue()}
          buttons={[{ label: LL().inbox.title(), href: "/inbox" }]}
        />
      }
      query={result}
    >
      {() => (
        <div class={containerClass}>
          <Show when={thread()} keyed>
            {threadData => (
              <>
                <div class={threadInfoClass}>
                  <div
                    class={threadTypeBannerClass({
                      variant: threadData.private ? "letter" : "comment",
                    })}
                  >
                    <Icon
                      name={threadData.private ? "mail-open" : "message-circle"}
                      size="14"
                    />
                    {threadData.private
                      ? LL().inbox.typePrivateEmail()
                      : LL().inbox.typePublicComment()}
                  </div>
                  <Show when={threadData.post} keyed>
                    {post => (
                      <>
                        <Icon
                          name={
                            post.publication?.id &&
                            post.publication.id !==
                              session()?.user?.publication?.id
                              ? "arrow-right"
                              : "arrow-left"
                          }
                          size="14"
                          color="sand400"
                        />
                        <span class={postReferenceLabelClass}>
                          {LL().inbox.inReplyTo()}
                        </span>
                        <a
                          href={post.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          class={postReferenceInlineClass}
                        >
                          {post.title} ↗
                        </a>
                        <Show
                          when={
                            post.publication?.id &&
                            post.publication.id !==
                              session()?.user?.publication?.id
                          }
                        >
                          <span class={postPublicationNameClass}>
                            · {post.publication?.name}
                          </span>
                        </Show>
                      </>
                    )}
                  </Show>
                </div>

                <div class={messagesListClass}>
                  <For each={threadData.messages}>
                    {(message, index) => (
                      <MessageCard
                        message={message}
                        ref={
                          index() === firstUnreadIndex()
                            ? el => {
                                firstUnreadEl = el
                              }
                            : undefined
                        }
                      />
                    )}
                  </For>
                </div>

                <div class={replyFormClass}>
                  <div
                    class={replyEditorWrapperClass}
                    onKeyDown={e => {
                      if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                        handleSubmit()
                      }
                    }}
                  >
                    <For each={[`${params.threadId}-${replyEditorKey()}`]}>
                      {() => (
                        <SimpleWysiwygEditor
                          content={() => null}
                          onUpdate={setReplyContent}
                          placeholder={LL().inbox.writeMessage()}
                          publicationName={
                            session()?.user?.publication?.name ?? ""
                          }
                          editable={!sendResult.fetching}
                          link={false}
                        />
                      )}
                    </For>
                  </div>
                  <Show when={sendErrorMessage()}>
                    {msg => <p class={replyErrorClass}>{msg()}</p>}
                  </Show>
                  <div class={replyActionsClass}>
                    <Button
                      hue={threadHue()}
                      onClick={handleSubmit}
                      disabled={!canSubmitReply()}
                    >
                      {sendResult.fetching
                        ? LL().inbox.publishing()
                        : LL().inbox.submit()}
                    </Button>
                  </div>
                </div>
              </>
            )}
          </Show>
        </div>
      )}
    </MainLayout>
  )
}

function MessageCard(props: {
  message: FragmentOf<typeof MessageCardFragment>
  ref?: (el: HTMLDivElement) => void
}) {
  const { LL, locale } = useI18nContext()
  const message = createFragment(MessageCardFragment, () => props.message)
  const relativeTime = useRelativeTime(() => message().createdAt)

  const [showTranslated, setShowTranslated] = createSignal(false)
  const [translatedContent, setTranslatedContent] = createSignal<string | null>(
    null,
  )
  const [translateState, executeTranslate] = createMutationWithErrorHandling(
    TranslateMessageMutation,
    "translateMessage",
  )

  const canTranslate = createMemo(
    () => message().locale != null && message().locale !== locale(),
  )

  async function handleTranslate() {
    if (showTranslated()) {
      setShowTranslated(false)
      return
    }
    if (translatedContent()) {
      setShowTranslated(true)
      return
    }
    const out = await executeTranslate(
      { messageId: message().id, toLocale: locale() },
      { muteGraphQlUnionToast: true },
    )
    if (out.success) {
      setTranslatedContent(out.data.translatedContent)
      setShowTranslated(true)
    }
  }

  return (
    <div ref={props.ref} class={messageCardClass}>
      <div class={messageHeaderClass}>
        <ThreadParticipants individual participants={[message().author]} />
      </div>
      <div class={messageContentClass}>
        <RichContent
          content={showTranslated() ? translatedContent()! : message().content}
        />
      </div>
      <div class={commentFooterClass}>
        <Show when={canTranslate()} fallback={<span />}>
          <button
            type="button"
            class={commentTranslateButtonClass}
            disabled={translateState.fetching}
            onClick={handleTranslate}
          >
            {translateState.fetching
              ? LL().common.translating()
              : showTranslated()
                ? LL().comments.showOriginal({
                    locale: getLocaleDisplayName(message().locale),
                  })
                : LL().comments.translateTo({
                    locale: getLanguageLabel(locale()),
                  })}
          </button>
        </Show>
        <div class={messageDateClass}>{relativeTime()}</div>
      </div>
    </div>
  )
}
